import { useCallback, useEffect, useRef, useState } from "react";

import { EstadoPerfil, getEstadoPerfil } from "src/services/operarioPerfil";

// Evento que disparan los componentes tras guardar el perfil para que el guard
// libere el bloqueo de inmediato sin esperar al próximo polling.
export const PERFIL_REFETCH_EVENT = "perfil-actualizacion-refetch";

const POLL_INTERVAL_MS = 60_000;

interface UseEstadoPerfilOptions {
  isAuth: boolean;
}

interface UseEstadoPerfilResult {
  estado: EstadoPerfil | null;
  needsUpdate: boolean;
  refetch: () => void;
}

// Mantiene en memoria si el operario necesita actualizar sus datos personales,
// según la campaña vigente (AE_PerfilVersionConfig + version_perfil). Política
// ante fallos de red: NO bloquear (mejor permitir uso que dejar la app
// inutilizable por un blip de conectividad).
export function useEstadoPerfil(
  options: UseEstadoPerfilOptions
): UseEstadoPerfilResult {
  const { isAuth } = options;
  const [estado, setEstado] = useState<EstadoPerfil | null>(null);
  const isAuthRef = useRef(isAuth);
  isAuthRef.current = isAuth;

  const fetchEstado = useCallback(async () => {
    if (!isAuthRef.current) return;
    try {
      const data = await getEstadoPerfil();
      if (!isAuthRef.current) return;
      setEstado(data);
    } catch {
      if (!isAuthRef.current) return;
      // No bloquear si el backend no responde: mantenemos el último estado conocido.
    }
  }, []);

  // Carga inicial al autenticarse + limpieza al deslogearse
  useEffect(() => {
    if (!isAuth) {
      setEstado(null);
      return;
    }
    fetchEstado();
  }, [isAuth, fetchEstado]);

  // Polling de fondo mientras la sesión esté activa (intervalo largo: el perfil
  // no cambia con tanta frecuencia como otros estados).
  useEffect(() => {
    if (!isAuth) return;
    const id = window.setInterval(() => {
      fetchEstado();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isAuth, fetchEstado]);

  // Refetch al volver al foreground
  useEffect(() => {
    if (!isAuth) return;
    const handler = () => {
      if (document.visibilityState === "visible") fetchEstado();
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [isAuth, fetchEstado]);

  // Refetch manual disparado tras guardar el perfil. Hacemos un optimistic clear
  // (needsUpdate=false) para liberar el guard antes de la respuesta del backend.
  useEffect(() => {
    const handler = () => {
      setEstado((prev) => (prev ? { ...prev, needsUpdate: false } : prev));
      fetchEstado();
    };
    window.addEventListener(PERFIL_REFETCH_EVENT, handler);
    return () => window.removeEventListener(PERFIL_REFETCH_EVENT, handler);
  }, [fetchEstado]);

  return {
    estado,
    needsUpdate: Boolean(estado?.needsUpdate),
    refetch: fetchEstado,
  };
}
